import React, { useEffect, useRef, useState } from "react";
import BackHeader from "@components/header/BackHeader";
import { FormProvider, useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { useNavigate, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { stockEditSchema } from "@libs/zod/zodVaildation";
import { useApiRequest } from "@utils/ApiRequest";
import { useMutation, useQuery } from "@tanstack/react-query";
import BasicButton from "@components/button/BasicButton";
import {
  ColorWithSize,
  ProductClassificationFields,
  ProductNameInput,
} from "@pages/register/_components/index";
import BasicModal from "@components/modal/BasicModal";
import { ModalContent } from "@components/modal/ModalContent";
import useModalStore from "@store/modalStore";
import { queryClient } from "@libs/reactQuery/queryClient";
import { Flex } from "antd";
import { buildClassificationPayload, createOptionKey } from "@utils/productClassification";

const StockRegister: React.FC = () => {
  // 1. State
  const navigate = useNavigate();
  const { getUserInfo, getProductDetail, getAiTrainingData, getColorList } =
    useApiRequest();
  const { modals, openModal, closeModal } = useModalStore();
  const { id, status } = useParams();
  const stockListRef = useRef<HTMLDivElement | null>(null);
  const { postProductStock, postProductStockTemp } = useApiRequest();
  const [errorContext, setErrorContext] = useState("처리");
  const [errorMessage, setErrorMessage] = useState("");
  // 등록 모드 구분 (2가지만)
  const isEditMode = !!(id && status); // 임시저장에서 등록
  const isNewMode = !isEditMode; // 새로 등록 (AI 데이터 포함)

  // console.log("isEditMode", isEditMode);
  // 2. Query
  const { data: productInfo } = useQuery({
    queryKey: ["productInfo", id, status],
    queryFn: () => getProductDetail(Number(id), status),
    enabled: isEditMode,
  });

  const { data: aiTrainingData } = useQuery({
    queryKey: ["aiTrainingData"],
    queryFn: () => getAiTrainingData(Number(id)),
    enabled: isNewMode,
  });

  const { data: colorList } = useQuery({
    queryKey: ["colorList"],
    queryFn: getColorList,
  });

  const { data: userInfo } = useQuery({
    queryKey: ["userInfo"],
    queryFn: getUserInfo,
  });

  const openErrorModal = (context: string, message: string) => {
    setErrorContext(context);
    setErrorMessage(message);
    openModal("errorModal");
  };

  const getMutationErrorMessage = (error: any, fallback: string) =>
    error?.response?.data?.data?.message || fallback;

  const buildFlatStockList = (stockList: any[] = []) =>
    stockList.flatMap((stock: any) =>
      (stock.size_list || []).map((size: any) => ({
        color_id: stock.color_id,
        option_key: stock.option_key || createOptionKey(),
        color_name_ko: stock.color_name_ko || stock.color_name || null,
        color_name_en: stock.color_name_en || null,
        color_name_ja: stock.color_name_ja || null,
        color_sku: stock.color_sku ?? "",
        size_sku: size.size_sku ?? "",
        color_hex_code: stock.hex_code || null,
        base_color_id: stock.base_color_id || null,
        size_id: size.size_id,
        amount: size.amount,
      })),
    );

  const buildTagPayload = (data: any) => {
    const typeTagIds = (data.tag_type_ids || []).map(Number).filter(Boolean);
    const tagIds = Array.from(
      new Set(
        [
          ...(data.tag_type_ids || []),
          ...(data.tag_style_ids || []),
          ...(data.tag_fit_ids || []),
          ...(data.tag_occasion_ids || []),
        ]
          .map(Number)
          .filter(Boolean),
      ),
    );

    return {
      type_tag_ids: typeTagIds,
      tag_ids: tagIds,
      tag_type_ids: typeTagIds,
      tag_style_ids: (data.tag_style_ids || []).map(Number).filter(Boolean),
      tag_fit_ids: (data.tag_fit_ids || []).map(Number).filter(Boolean),
      tag_occasion_ids: (data.tag_occasion_ids || []).map(Number).filter(Boolean),
    };
  };

  const { mutate } = useMutation({
    mutationFn: (data: any) => postProductStock(data),
    onSuccess: (data) => {
      const productId = data.product_id;
      navigate(`/stock-success/${Number(productId)}/STOCK`, { replace: true });
      queryClient.invalidateQueries({
        queryKey: ["productInfo", id, status],
      });
      queryClient.invalidateQueries({
        queryKey: ["getProductInventorySimple"],
      });
    },
    onError: (error: any) => {
      openErrorModal(
        "등록",
        getMutationErrorMessage(error, "재고 등록에 실패했습니다."),
      );
    },
  });

  const { mutate: postProductStockTempMutate } = useMutation({
    mutationFn: (data: any) => postProductStockTemp(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["productInfo"],
      });
      navigate("/capture-list", { replace: true });
    },
    onError: (error: any) => {
      openErrorModal(
        "임시저장",
        getMutationErrorMessage(error, "임시저장에 실패했습니다."),
      );
    },
  });

  // 3. Function
  const handleBackEvent = (): void => {
    openModal("backModal");
  };

  const handleTempSave = async () => {
    if (!userInfo?.id) {
      openErrorModal("임시저장", "사용자 정보를 불러오지 못했습니다.");
      return;
    }

    const data = getValues();
    const categoryId =
      data.depth1 || productInfo?.category_depth1_id || aiTrainingData?.category_depth1_id ||
      aiTrainingData?.category_id;
    const postStockList = buildFlatStockList(data.stock_list);

    const tempPostData = {
      product_id: isEditMode ? productInfo?.product_id : null,
      user_id: userInfo.id,
      category_id: categoryId,
      name_ko: data.name_ko,
      name_en: data.name_en,
      thumbnail_url: data.thumbnail_url,
      classification: buildClassificationPayload({
        audienceCategoryIds: (data.audience_category_ids || []).map(Number),
        primaryColorOptionKey: data.stock_list?.[0]?.option_key || postStockList?.[0]?.option_key || null,
        patterns: data.patterns || [],
      }),
      image_list: data.image_list,
      stock_list: postStockList,
      ...buildTagPayload(data),
    };

    // console.log("tempPostData", tempPostData);
    postProductStockTempMutate(tempPostData);
  };

  // 4. useForm
  const method = useForm({
    resolver: zodResolver(stockEditSchema),

    mode: "onChange",
    reValidateMode: "onChange",

    defaultValues: {
      product_id: productInfo?.product_id || null,
      brand_id: productInfo?.brand_id || null,
      size_type_id: productInfo?.size_type_id,

      depth1: productInfo?.category_depth1_id || productInfo?.category_id || null,
      name_ko: productInfo?.product_name_ko || "",
      name_en: productInfo?.product_name_en || "",
      primary_color_id: productInfo?.primary_color_id ?? null,
      pattern: productInfo?.pattern || "",
      tag_type_ids: productInfo?.tag_type_ids || [],
      tag_style_ids: productInfo?.tag_style_ids || [],
      tag_fit_ids: productInfo?.tag_fit_ids || [],
      tag_occasion_ids: productInfo?.tag_occasion_ids || [],
      tag_color_pattern_ids: productInfo?.tag_color_pattern_ids || [],
      audience_category_ids: [] as number[],
      patterns: [] as any[],

      stock_list:
        productInfo?.color_list?.map((color: any) => ({
          color_id: color.base_color_id ? null : color.color_id,
          option_key: color.option_key || createOptionKey(),
          color_name: color.name,
          color_name_ko: color.name,
          color_name_en: color.name_en,
            color_name_ja: color.name_ja,
            color_sku: color.color_sku || "",
          base_color_id: color.base_color_id,
          hex_code: color.hex_code,
          size_list:
            color?.size_list?.map((size: any) => ({
              size_id: size.id,
              size_name: size.name,
                size_sku: size.size_sku || "",
              amount: size.amount,
            })) || [],
        })) || [],
      image_list:
        productInfo?.product_image_list.map((item: any) => item.image_url) ||
        [],

      thumbnail_url: productInfo?.product_image_list?.[0]?.image_url || "",
    },
  });

  const {
    handleSubmit,
    reset,
    setValue,
    getValues,
  } = method;

  // 폼 제출 처리
  const onSubmit = async (data: any) => {
    if (!userInfo?.id) {
      openErrorModal("등록", "사용자 정보를 불러오지 못했습니다.");
      return;
    }

    const resolvedCategoryId =
      data.depth1 || productInfo?.category_depth1_id || aiTrainingData?.category_depth1_id ||
      aiTrainingData?.category_id;
    const postStockList = buildFlatStockList(data.stock_list);

    const postData = {
      product_id: isEditMode ? productInfo?.product_id : null,
      user_id: userInfo.id,
      category_id: resolvedCategoryId,

      name_ko: data.name_ko,
      name_en: data.name_en,
      thumbnail_url: data.thumbnail_url,
      classification: buildClassificationPayload({
        audienceCategoryIds: (data.audience_category_ids || []).map(Number),
        primaryColorOptionKey: data.stock_list?.[0]?.option_key || postStockList?.[0]?.option_key || null,
        patterns: data.patterns || [],
      }),
      image_list: data.image_list,
      stock_list: postStockList,
      ...buildTagPayload(data),
    };

    // console.log("postData", postData);
    mutate(postData);
  };

  // 5. useEffect

  // 임시 저장 데이터 상품등록 할 때
  useEffect(() => {
    if (productInfo && isEditMode) {
      reset({
        product_id: productInfo.product_id || null,
        size_type_id: productInfo.size_type_id,
        brand_id: productInfo.brand_id,
        name_ko: productInfo.product_name_ko || "",
        name_en: productInfo.product_name_en || "",
        depth1: productInfo.category_depth1_id || productInfo.category_id || null,
        primary_color_id: productInfo.primary_color_id ?? null,
        pattern: productInfo.pattern || "",
        tag_type_ids: productInfo.tag_type_ids || [],
        tag_style_ids: productInfo.tag_style_ids || [],
        tag_fit_ids: productInfo.tag_fit_ids || [],
        tag_occasion_ids: productInfo.tag_occasion_ids || [],
        tag_color_pattern_ids: productInfo.tag_color_pattern_ids || [],
        audience_category_ids:
          productInfo.classification?.audience_categories?.map((item: any) => Number(item.id)) ||
          [productInfo.category_depth1_id || productInfo.category_id].filter(Boolean),
        patterns: productInfo.classification?.patterns ||
          (productInfo.pattern ? [{ tag_id: null, name_ko: productInfo.pattern }] : []),

        stock_list:
          productInfo.color_list?.map((color: any) => ({
            color_id: color.base_color_id ? null : color.color_id,
            option_key: color.option_key || createOptionKey(),
            color_name: color.name,
            color_name_ko: color.name,
            color_name_en: color.name_en,
            color_name_ja: color.name_ja,
            color_sku: color.color_sku || "",
            base_color_id: color.base_color_id,
            hex_code: color.hex_code,
            size_list:
              color.size_list?.map((size: any) => ({
                size_id: size.id,
                size_name: size.name,
                size_sku: size.size_sku || "",
                amount: size.amount,
              })) || [],
          })) || [],

        image_list: productInfo.product_image_list.map(
          (item: any) => item.image_url,
        ),
        thumbnail_url: productInfo.product_image_list?.[0]?.image_url || "",
      });
    }
  }, [productInfo, method, reset, isEditMode]);

  // 새로 등록할 때
  useEffect(() => {
    if (aiTrainingData && isNewMode) {
      const matchedColor = colorList?.find(
        (c: any) => Number(c.id) === Number(aiTrainingData.color_id),
      );

      setValue("size_type_id", aiTrainingData.size_type_id || "");
      setValue(
        "depth1",
        aiTrainingData.category_depth1_id || aiTrainingData.category_id || null,
      );
      setValue("primary_color_id", aiTrainingData.color_id || null);
      setValue("pattern", aiTrainingData.pattern || "");
      setValue("tag_type_ids", aiTrainingData.tag_type || []);
      setValue("tag_style_ids", aiTrainingData.tag_style || []);
      setValue("tag_fit_ids", aiTrainingData.tag_fit || []);
      setValue("tag_occasion_ids", aiTrainingData.tag_occasion || []);
      setValue("tag_color_pattern_ids", []);
      setValue("audience_category_ids", aiTrainingData.classification?.audience_category_ids || [aiTrainingData.category_depth1_id || aiTrainingData.category_id].filter(Boolean));
      setValue("patterns", aiTrainingData.classification?.patterns || (aiTrainingData.pattern ? [{ tag_id: null, name_ko: aiTrainingData.pattern }] : []));
      setValue(
        "image_list",
        aiTrainingData.image_url ? [aiTrainingData.image_url] : [],
      );
      setValue("thumbnail_url", aiTrainingData.image_url || "");
      setValue("stock_list", [
        {
          ...(aiTrainingData.color_input || {}),
          color_id: aiTrainingData.color_input?.color_id ?? aiTrainingData.color_id,
          option_key: createOptionKey(),
          color_name: aiTrainingData.color_input?.color_name_ko || aiTrainingData.color_input?.color_name_en || matchedColor?.name,
          hex_code: aiTrainingData.color_input?.color_hex_code || matchedColor?.hex_code,
          size_list: [],
        },
      ]);
    }
  }, [aiTrainingData, reset, setValue, colorList, isNewMode]);

  // 로딩 중일 때는 빈 컴포넌트 반환
  return (
    <FormProvider {...method}>
      <BackHeader title="재고 등록" onClick={handleBackEvent} />

      <Container>
        <Flex vertical gap="2rem">
          <ProductNameInput />

          <ProductClassificationFields />

          <ColorWithSize stockListRef={stockListRef} />
        </Flex>

        <ButtonWrapper>
          <BasicButton
            title="재고 등록"
            height="5.2rem"
            width="100%"
            padding="1.6rem 1.2rem"
            onClick={handleSubmit(() => openModal("registerModal"))}
          />
        </ButtonWrapper>

        <BasicModal
          open={modals.registerModal}
          activeName="확인"
          activeEvent={() => {
            closeModal("registerModal");
            handleSubmit(onSubmit)();
          }}
          closeName="취소"
          closeModal={() => closeModal("registerModal")}>
          <ModalContent
            mainMessage="작성하신 내용으로 상품을"
            subMessage="재고에 등록하시겠습니까?"
          />
        </BasicModal>

        {/* 뒤로가기 모달 */}
        <BasicModal
          open={modals.backModal}
          activeName="확인"
          activeEvent={() => {
            closeModal("backModal");
            handleTempSave();
          }}
          closeName="취소"
          closeModal={() => closeModal("backModal")}>
          <ModalContent
            mainMessage="현재까지 작성한 정보를 저장하고"
            subMessage="이전으로 돌아가시겠습니까?"
          />
        </BasicModal>

        <BasicModal
          open={modals.errorModal}
          activeName="확인"
          notice
          activeEvent={() => closeModal("errorModal")}
          closeModal={() => closeModal("errorModal")}>
          <ModalContent
            mainMessage={`재고 ${errorContext} 중 오류가 발생했습니다`}
            subMessage={errorMessage}
          />
        </BasicModal>
      </Container>
    </FormProvider>
  );
};

export default StockRegister;

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "3rem",
  height: "calc(100vh - 5.6rem)",
  padding: "2rem 2rem 0 2rem",
  justifyContent: "space-between",
});

const ButtonWrapper = styled.div({
  width: "100%",
  paddingBottom: "2rem",
});
